import React, { useState, useEffect } from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Button } from '@mui/material';
import { useModal } from '../../../contexts/ModalContext';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { fetchPaymentModeSelect, fetchPaymentStatusSelect } from '../../../services/common';
import AddBookingPaymentDialog from './AddBookingPaymentDialog';
import EditBookingPaymentDialog from './EditBookingPaymentDialog';
import FileUploadModal from './FileUploadModal';
import { formatDate, bookingTokens, cardSx, cardHeadSx, tableHeadSx, emptyStateSx, primaryButtonSx, ghostButtonSx } from '../bms_helper';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const BookingPayments = ({ booking, onActionComplete, emptyMessage, paymentModes: propPaymentModes, paymentStatuses: propPaymentStatuses  }) => {
  const { bookingPayments, fetchBookingPayments, deleteBookingPayment, addBookingPayment, attachProof } = useBookingDetails();
  const { openConfirmModal, openSuccessModal, openErrorModal } = useModal();
  const [editingPayment, setEditingPayment] = useState(null);
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState(propPaymentModes || []);
  const [paymentStatuses, setPaymentStatuses] = useState(propPaymentStatuses || []);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedPaymentForProof, setSelectedPaymentForProof] = useState(null);

  // Fetch payments when component mounts
  useEffect(() => {
    if (booking?.id) {
      fetchBookingPayments(booking.id);
    }
  }, [booking?.id, fetchBookingPayments]);

  // Fetch payment modes and statuses if not provided as props
  useEffect(() => {
    const fetchOptions = async () => {
      if (propPaymentModes && propPaymentStatuses) {
        setPaymentModes(propPaymentModes);
        setPaymentStatuses(propPaymentStatuses);
        return;
      }

      setLoadingOptions(true);
      try {
        const [modes, statuses] = await Promise.all([
          fetchPaymentModeSelect(),
          fetchPaymentStatusSelect()
        ]);
        setPaymentModes(modes || []);
        setPaymentStatuses(statuses || []);
      } catch (error) {
        console.error('Error fetching payment options:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchOptions();
  }, [propPaymentModes, propPaymentStatuses]);
  
  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
  };

  const handleAddPayment = () => {
    setAddPaymentDialogOpen(true);
  };

  const handleAddPaymentClose = async (result) => {
    setAddPaymentDialogOpen(false);
    if (result === 'added' || result === 'updated') {
      // Refresh the payments data
      if (booking?.id) {
        await fetchBookingPayments(booking.id);
      }
      if (onActionComplete) {
        onActionComplete();
      }
    }
  };

  const handleAttachProof = (payment) => {
    setSelectedPaymentForProof(payment);
    setUploadModalOpen(true);
  };

  const handleUploadProof = async (file) => {
    try {
      await attachProof(booking.id, 'booking', selectedPaymentForProof.id, file);
      openSuccessModal('Success', 'Payment proof uploaded successfully');
    } catch (error) {
      openErrorModal('Error', error.message || 'Failed to upload payment proof');
      throw error;
    }
  };

  const handleDeletePayment = async (payment) => {
    openConfirmModal(
      'Delete Payment',
      'Are you sure you want to delete this payment? This action cannot be undone.',
      async () => {
        try {
          await deleteBookingPayment(
            booking.id,
            payment.id
          );
          
          // Show success message
          openSuccessModal(
            'Payment Deleted',
            'The payment has been successfully deleted.'
          );

          // Refresh the payments data
          if (booking?.id) {
            await fetchBookingPayments(booking.id);
          }

        } catch (error) {
          openErrorModal(
            'Delete Failed',
            error.message || 'Failed to delete the payment. Please try again.'
          );
        }
      },
      'Delete'  // confirmLabel
    );
  };

  const handlePaymentDialogClose = async (result) => {
    if (result === 'deleted' || result === 'updated') {
      // Refresh the payments data
      if (booking?.id) {
        await fetchBookingPayments(booking.id);
      }
    }
    setEditingPayment(null);
  };

  return (
    <>
    {loadingOptions ? (
      <Paper sx={{ width: '100%', p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading payment options...
        </Typography>
      </Paper>
    ) : (
    <Paper elevation={0} sx={{ ...cardSx, width: '100%', overflow: 'hidden' }}>
      <Box sx={cardHeadSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography component="span" sx={{ fontSize: 14 }}>💳</Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: bookingTokens.ink }}>Booking Payments</Typography>
        </Box>
        {booking.status !== 'cancelled' && booking.status !== 'completed' && booking.status !== 'draft' && (() => {
          const totalPrice = Number(booking?.sale_order?.total_price || booking?.total_price || 0);
          const totalPaid = Number(booking?.payment_breakdown?.total_paid || booking?.total_paid || 0);
          return Math.max(0, totalPrice - totalPaid) > 0;
        })() && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddPayment} sx={primaryButtonSx}>Add Payment</Button>
        )}
      </Box>
    <TableContainer>
      <Table size="small" sx={{ '& thead tr': { background: '#f8fbf9' }, '& th': { padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.55px', color: bookingTokens.subtle, borderBottom: `1.5px solid ${bookingTokens.border}` } }}>
        <TableHead>
          <TableRow>
            <TableCell>Payment ID</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Reference</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookingPayments && bookingPayments.length > 0 ? (
            bookingPayments.map((payment) => (
              <TableRow key={payment.payment_id} sx={{ '&:hover td': { bgcolor: '#f2f8f4' } }}>
                <TableCell sx={{ fontWeight: 700, color: bookingTokens.green }}>{payment.payment_id}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>₹{Number(payment.amount).toLocaleString()}</TableCell>
                <TableCell>
                  {formatDate(payment.payment_date)}
                </TableCell>
                <TableCell><Box component="span" sx={{ bgcolor: bookingTokens.bg, border: `1px solid ${bookingTokens.border}`, fontSize: 11.5, fontWeight: 600, px: 1.125, py: 0.375, borderRadius: 1 }}>{payment.payment_method_verbose || payment.payment_method || '-'}</Box></TableCell>
                <TableCell sx={{ color: bookingTokens.muted }}>{payment.transaction_reference || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {payment.payment_proof ? (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        href={payment.payment_proof}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        View
                      </Button>
                    ) : (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAttachProof(payment)}
                        sx={{ p: 0.5 }}
                      >
                        <AttachFileIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePayment(payment)}
                      sx={{ p: 0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 0, border: 'none', verticalAlign: 'middle' }}>
                <Box sx={emptyStateSx}>
                  <Typography sx={{ fontSize: 40, opacity: 0.4 }}>💸</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: bookingTokens.muted }}>{emptyMessage || 'No payments available.'}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
    </Paper>
    )}
    <EditBookingPaymentDialog
      open={editingPayment !== null}
      onClose={handlePaymentDialogClose}
      booking={booking}
      payment={editingPayment}
      paymentModes={paymentModes}
      paymentStatuses={paymentStatuses}
    />
    
    <AddBookingPaymentDialog
      open={addPaymentDialogOpen}
      onClose={handleAddPaymentClose}
      booking={booking}
      pendingAmount={(() => {
        const totalPrice = Number(booking?.sale_order?.total_price || booking?.total_price || 0);
        const totalPaid = Number(booking?.payment_breakdown?.total_paid || booking?.total_paid || 0);
        return Math.max(0, totalPrice - totalPaid);
      })()}
      paymentModes={paymentModes}
    />

    <FileUploadModal
      open={uploadModalOpen}
      onClose={() => setUploadModalOpen(false)}
      onUpload={handleUploadProof}
      title="Upload Payment Proof"
    />
    </>
  );
};

export default BookingPayments;
